import React, { CSSProperties, useEffect, useState } from 'react';

import { t } from 'i18next';
import { DateTime } from 'luxon';

import {
  TeacherGetAttendancesQuery,
  useClassStudentsQuery,
  useCreateAttendanceMutation,
  useDeleteAttendancesMutation,
  useGetAttendanceTypesQuery,
} from '@school_ify/schoolify-repository/src/teacher/types';

import { ReactComponent as Floppy } from 'assets/icons/floppy-new.svg';
import { ReactComponent as DateIcon } from 'assets/icons/datepicker-new.svg';
import { ReactComponent as AbsentIcon } from 'assets/icons/attendance_absent.svg';
import { ReactComponent as LateIcon } from 'assets/icons/attendance_late.svg';
import { ReactComponent as ExemptIcon } from 'assets/icons/attendance_exempt.svg';
import { ReactComponent as RemarkIcon } from 'assets/icons/attendance_remark.svg';
import StyledTooltip from 'components/StyledTooltip/StyledTooltip';
import ModalDialog from 'components/ModalDialog/ModalDialog';
import CheckBox from 'clean/components/common/CheckBox/CheckBox';

import './AttendanceModal.scoped.scss';

interface Props {
  style?: CSSProperties;
  openModal?: boolean;
  handleModal?: () => void;
  refetchAttendances: () => void;
  subjectId: string;
  date: string;
  attendances?: TeacherGetAttendancesQuery['attendances'];
  classId?: string;
  onInputChange?: (id: string, mark: number) => void;
  semesterId: string;
  classTitle: string;
  subjectTitle: string;
}

const AttendanceModal: React.FC<Props> = ({
  openModal,
  handleModal,
  date,
  semesterId,
  subjectId,
  classId,
  attendances,
  refetchAttendances,
  classTitle,
  subjectTitle,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const { data: studentsData } = useClassStudentsQuery({
    variables: {
      class_id: classId,
    },
  });

  const [formData, setFormData] = useState<
    { studentId: string; selectedType: string; comment: string; attendanceIds: string[] }[]
  >([]);

  const { data: attendanceTypeData } = useGetAttendanceTypesQuery();
  const attendanceTypes = attendanceTypeData?.attendance_types || [];

  const students = (studentsData?.student_class || [])
    .filter(i => i.user.length)
    .map(u => u.user[0]);

  useEffect(() => {
    if (students.length) {
      const result = [];
      students.forEach(student => {
        const attendanceItems = attendances.filter(item => {
          const dateStr = DateTime.fromISO(item.date).toFormat('y-MM-dd');
          return (
            date === dateStr && item.subject_id === subjectId && student.id === item.student_id
          );
        });
        const firstAttendance = attendanceItems.length > 0 ? attendanceItems[0] : undefined;
        result.push({
          studentId: student.id,
          selectedType: firstAttendance?.attendance_type || '',
          comment: firstAttendance?.comment || '',
          attendanceIds: attendanceItems.map(i => i.id),
        });
      });
      setFormData(result);
    }
  }, [studentsData]);

  const setField = (studentId, field, value) => {
    setFormData(
      [...formData].map(item => {
        if (item.studentId === studentId) {
          return {
            ...item,
            [field]: field === 'selectedType' && item.selectedType === value ? null : value,
          };
        }
        return item;
      })
    );
  };
  const [createAttendance] = useCreateAttendanceMutation();
  const [deleteAttendance] = useDeleteAttendancesMutation();
  const onSave = async () => {
    setIsSaving(true);
    try {
      const idsToDelete = [];

      formData.forEach(item => {
        item.attendanceIds.map(id => idsToDelete.push(id));
      });

      await deleteAttendance({
        variables: {
          where: {
            id: { _in: idsToDelete },
          },
        },
      });

      await createAttendance({
        variables: {
          attendances: formData
            .filter(item => item.selectedType || item.comment)
            .map(item => {
              return {
                attendance_type: item.selectedType === '' ? null : item.selectedType,
                class_id: classId,
                comment: item.comment,
                date,
                semester_id: semesterId,
                student_id: item.studentId,
                subject_id: subjectId,
              };
            }),
        },
      });

      handleModal();
    } catch (err) {
      console.error(err);
    }
    refetchAttendances();
    setIsSaving(false);
  };

  const icons = {
    absent: <AbsentIcon />,
    late: <LateIcon />,
    exempt: <ExemptIcon />,
    remark: <RemarkIcon />,
  };
  if (formData.length === 0) {
    return null;
  }
  return (
    <ModalDialog
      open={openModal}
      onClose={handleModal}
      title={t('attendance:attendance')}
      hasContentPadding={false}
      isLoadingActions={isSaving}
      actions={[
        {
          type: 'filled',
          colour: 'primary',
          label: (
            <div>
              <Floppy
                className="button-img"
                style={{ marginRight: 10, position: 'relative', top: '2px' }}
              />
              {t('common:save')}
            </div>
          ),
          handler: onSave,
        },
      ]}
      actionsJustifyContent="left"
    >
      <div className="grading-main-container">
        <div className="grading-top-section">
          <div className="grading-ts-middle" style={{ paddingTop: '27px', paddingLeft: '25px' }}>
            <p className="text-label">{t('grades:class')}</p>
            <div className="default-container">
              <span>{classTitle}</span>
            </div>
          </div>
          <div className="grading-ts-middle" style={{ flexGrow: 1, paddingTop: '27px' }}>
            <p className="text-label">{t('attendance:subject')}</p>
            <div className="default-container">
              <span>{subjectTitle}</span>
            </div>
          </div>
          <div className="grading-ts-middle">
            <p className="text-label">{t('common:Date')}</p>
            <div className="date-icon-container">
              <DateIcon />
              <span>{DateTime.fromISO(date).toFormat('dd.MM.y')}</span>
            </div>
          </div>
        </div>
        <div className="grading-container">
          <table className="grading-table">
            <thead>
              <tr className="tr-1">
                <th className="th-1">
                  <p className="th-title tr-title">{t('live_classes:Student')}</p>
                </th>
                {attendanceTypes.map(type => (
                  <th
                    className="checkboxCellTh"
                    key={type.value}
                    style={{ textAlign: 'center', paddingLeft: '0px' }}
                  >
                    <StyledTooltip direction="up" tagName="div" content={<p>{type?.text}</p>}>
                      {icons[type.icon]}
                    </StyledTooltip>
                  </th>
                ))}

                <th className="th-3">
                  <p className="th-title tr-title">{t('study:Comment')}</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {students?.map(student => {
                const data = formData.find(i => i.studentId === student.id);

                return (
                  <tr key={student.id}>
                    <td className="th-1">
                      <p>{student.full_name}</p>
                    </td>
                    {attendanceTypes.map(type => (
                      <td
                        className="checkboxCellTd"
                        key={type.value}
                        onClick={() => setField(student.id, 'selectedType', type.value)}
                      >
                        <div>
                          <CheckBox checked={type.value === data.selectedType} color="orange" />
                        </div>
                      </td>
                    ))}

                    <td className="th-1 td-2">
                      <input
                        className="comment-input"
                        placeholder={t('study:Comment')}
                        value={data.comment}
                        onChange={e => setField(student.id, 'comment', e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </ModalDialog>
  );
};

export default AttendanceModal;
